// "Production readiness": a check the admin runs on demand. It asks OUR server what
// it would do; nothing is sent to Defra and no credential or value is ever shown,
// only yes/no and the reasons a real submission would be refused right now.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, X } from 'lucide-react';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { fetchDwtReadiness, type DwtReadiness } from '../../../api/dwt';
import shared from '../../dwt/dwt.module.css';

const yn = (v: boolean): JSX.Element =>
  v ? (
    <span className={shared.row}>
      <Check size={14} aria-hidden /> Yes
    </span>
  ) : (
    <span className={shared.row}>
      <X size={14} aria-hidden /> No
    </span>
  );

function Facts({ r }: { r: DwtReadiness }): JSX.Element {
  const prod = r.company.environment === 'production';
  return (
    <dl className={shared.facts} aria-label="Readiness facts">
      <dt className={shared.factLabel}>Company environment</dt>
      <dd className={shared.factValue}>{prod ? 'Production' : 'Test'}</dd>
      <dt className={shared.factLabel}>Reporting mode</dt>
      <dd className={shared.factValue}>{r.company.mode === 'off' ? 'Off' : r.company.mode === 'manual' ? 'Manual' : 'Automatic'}</dd>
      <dt className={shared.factLabel}>API code entered</dt>
      <dd className={shared.factValue}>{yn(r.company.api_code_set)}</dd>
      <dt className={shared.factLabel}>Start date set</dt>
      <dd className={shared.factValue}>{yn(r.company.start_date_set)}</dd>
      <dt className={shared.factLabel}>Profiles confirmed</dt>
      <dd className={shared.factValue}>
        {r.company.profiles_confirmed} confirmed, {r.company.profiles_unconfirmed} not confirmed
      </dd>
      <dt className={shared.factLabel}>Production credentials on the server</dt>
      <dd className={shared.factValue}>{yn(r.server.production_credentials_present)}</dd>
      <dt className={shared.factLabel}>Live reporting allowed (DWT_ALLOW_PRODUCTION)</dt>
      <dd className={shared.factValue}>{yn(r.server.dwt_allow_production)}</dd>
      <dt className={shared.factLabel}>Outbound mode (OUTBOUND_MODE)</dt>
      <dd className={shared.factValue}>{r.server.outbound_mode}</dd>
    </dl>
  );
}

export function ProductionReadiness(): JSX.Element {
  const [run, setRun] = useState(false);
  const q = useQuery({ queryKey: ['dwt', 'readiness'], queryFn: fetchDwtReadiness, enabled: run, retry: false, staleTime: 0 });

  return (
    <Card title="Production readiness">
      <div className={shared.stack}>
        <p className={shared.muted} style={{ margin: 0 }}>
          Checks what this server would do right now. Nothing is sent to Defra.
        </p>
        <div className={shared.row}>
          <Button variant="secondary" onClick={() => (run ? void q.refetch() : setRun(true))} loading={q.isFetching}>
            {run ? 'Check again' : 'Check production readiness'}
          </Button>
        </div>
        {q.isError ? <p role="alert">The check could not be run.</p> : null}
        {q.data ? (
          <>
            {q.data.ready ? (
              <div className={`${shared.banner} ${shared.bannerInfo}`} role="status">
                <span className={shared.bannerTitle}>Nothing would stop a real submission right now.</span>
              </div>
            ) : (
              <div className={`${shared.banner} ${shared.bannerWarning}`} role="status">
                <span className={shared.bannerTitle}>A real submission would be refused because:</span>
                <ul className={shared.list}>
                  {q.data.refusals.map((x) => (
                    <li key={x.code}>{x.message}</li>
                  ))}
                </ul>
              </div>
            )}
            <Facts r={q.data} />
          </>
        ) : null}
      </div>
    </Card>
  );
}
